const prefix = import.meta.env.VITE_API_URL;
import { useStore } from "./store";
import { navigate } from "wouter/use-browser-location";
import { notifications } from "@mantine/notifications";
import mixpanel from "mixpanel-browser";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

function getAuthHeaders() {
  const authToken = useStore.getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

export async function jsonApiRequest(method, uri, values, signal = null) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
    signal: signal,
  };

  // if (enableDebug) console.debug("executing request", reqDetails);

  try {
    const response = await fetch(reqDetails.url, {
      method: reqDetails.method,
      headers: reqDetails.headers,
      body: reqDetails.body,
      signal: reqDetails.signal,
    });

    // if (enableDebug) console.debug("got server response", response);

    if (response.status === 401) {
      useStore.getState().setAuthToken(null);
      useStore.getState().setProfile(null);
      navigate("/", { replace: true });
      notifications.show({
        color: "red",
        autoClose: 4000,
        title: "You are logged out",
        message: "Please login again",
      });
    }

    if (!response.ok) {
      var responseText = await response.text();

      // Try to extract a friendly message from ProblemDetails JSON. Prefer the specific
      // `detail` (e.g. which template parameter is invalid and the suggested fix) over the
      // generic `title`, falling back to the title when no detail is provided.
      let friendlyMessage;
      try {
        const problemDetails = JSON.parse(responseText);
        friendlyMessage = problemDetails.detail || problemDetails.title;
      } catch {
        // Response body was not JSON; fall through to the raw status text.
      }

      const message = friendlyMessage || `${response.status}: ${response.statusText}\n${responseText}`;

      notifications.show({
        color: "red",
        autoClose: 4000,
        title: "An error occurred!",
        message: message,
      });
      throw new Error(message);
    }

    if (response.status === 204) return {};

    const json = await response.json();
    // if (enableDebug) console.debug("response json", json);

    return json;
  } catch (error) {
    // Re-throw abort errors without showing notifications
    if (error.name === 'AbortError') {
      throw error;
    }
    // For other errors, let them propagate normally
    throw error;
  }
}

export async function jsonPost(uri, values, signal = null) {
  return await jsonApiRequest("POST", uri, values, signal);
}

export async function jsonGet(uri, signal = null) {
  return await jsonApiRequest("GET", uri, null, signal);
}

export async function jsonPut(uri, values, signal = null) {
  return await jsonApiRequest("PUT", uri, values, signal);
}

export async function jsonDelete(uri, signal = null) {
  return await jsonApiRequest("DELETE", uri, null, signal);
}

// export async function tenantRegister(dto) {
// dto: { tenantName, emailAddress, password }
// return await jsonPost("tenants", dto);
// }

export async function userRegister(dto) {
  mixpanel.track("User Register");
  return await jsonPost("auth/account", dto);
}

// This is used for the "verbatim call"
// export async function sendLlmMessage(dto) {
//   return await jsonPost(`verbatim-invocation`, dto);
// }

// Used for invocationLog page

// Helper function to serialize non-empty params
function serializeParams(params) {
  return Object.entries(params)
    .filter(
      ([, value]) =>
        value != null &&
        value !== "" &&
        (Array.isArray(value) ? value.length > 0 : true)
    )
    .map(([key, value]) => {
      // Handle arrays by emitting repeated keys (ASP.NET [FromQuery] List<string> binding)
      if (Array.isArray(value)) {
        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join("&");
      }
      // Handle Date objects by converting to ISO string
      let val;
      if (value instanceof Date) {
        val = value.toISOString();
      } else {
        val = value;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
    })
    .join("&");
}

export async function getLlmMessageLog({ id }) {
  const data = await jsonGet(`llm-logs/${id}`);
  // The API sends a flat deduplicated messages array; reconstruct
  // per-request messages so downstream components work unchanged.
  if (data.messages && data.requests) {
    for (const req of data.requests) {
      req.messages = data.messages.slice(0, req.messageCount ?? data.messages.length);
    }
  }
  return data;
}

export async function getLogEntriesForRequest(requestId) {
  return await jsonGet(`llm-logs/${requestId}/log-entries`);
}

// Stops a run and every sub-agent under it. Cooperative: the run notices between tool-calling
// iterations, so it finishes the model call already in flight first. Returns { signalled, message }
// — signalled is how many requests were told to stop, and 0 just means the run had already ended.
export const cancelRun = async (id) => {
  mixpanel.track("Run Cancelled");
  return await jsonPost(`runs/${id}/cancel`);
};

// Tenant-wide emergency stop. Reaches panels and bulk calls too, which run as independent roots
// and so cannot be stopped by cancelling "the run".
export const cancelAllRuns = async () => {
  mixpanel.track("All Runs Cancelled");
  return await jsonPost(`runs/cancel-all`);
};

export const getDashboardStats = async (period, signal = null) =>
  await jsonGet(`statistics?period=${period}`, signal);

export const getFlowStats = async (flowName, period, signal = null) =>
  await jsonGet(`statistics/flow/${flowName}?period=${period}`, signal);

export const getFlowStatsExtended = async (flowName, period, signal = null) =>
  await jsonGet(`statistics/flow/${flowName}/extended?period=${period}`, signal);

export const getAccountOverview = async (period, signal = null) =>
  await jsonGet(`statistics/overview?period=${period}`, signal);

export const getModelCosts = async (period, flowName = null, signal = null) => {
  const params = new URLSearchParams({ period: String(period) });
  if (flowName) params.set("flowName", flowName);
  return await jsonGet(`statistics/model-costs?${params}`, signal);
};

export const getPendingCallsCount = async () =>
  await jsonGet(`statistics/pending-count`);

// Admin-only: cost per provider per calendar month across all tenants.
export const getProviderCosts = async (months, signal = null) =>
  await jsonGet(`statistics/provider-costs?months=${months}`, signal);

// Flow x model cost breakdown for everything carrying one exact tag (e.g. "TenderId:12313").
// grouping: "split" (a row per flow that ran) or "rollup" (sub-agents folded into the entry flow).
export const getTagCostMatrix = async (
  { tag, grouping, from, to },
  signal = null
) =>
  await jsonGet(
    `statistics/tag-costs?${serializeParams({ tag, grouping, from, to })}`,
    signal
  );

// One row per distinct tag under a key prefix (e.g. "TenderId:"), ranked by spend.
export const getTagCostsByKey = async (
  { prefix, from, to, limit },
  signal = null
) =>
  await jsonGet(
    `statistics/tag-costs/by-key?${serializeParams({ prefix, from, to, limit })}`,
    signal
  );

export async function getLlmMessageLogs(filters) {
  mixpanel.track("Get LLM Message Logs");

  const params = serializeParams({
    cursor: filters.cursor,
    pageSize: filters.pageSize || 50,
    statuses: filters.statuses,
    environments: filters.environments,
    flows: filters.flows,
    tags: filters.tags,
    from: filters.from,
    to: filters.to,
    rootOnly: filters.rootOnly,
    parentRequestId: filters.parentRequestId,
  });

  return await jsonGet(`llm-logs${params ? `?${params}` : ""}`);
}

export async function getRequestHierarchy(requestId) {
  return await jsonGet(`llm-logs/${requestId}/hierarchy`);
}

export async function getAgentTreeForLog(requestId) {
  return await jsonGet(`llm-logs/${requestId}/agent-tree`);
}

export async function getToolEventsForRequest(requestId) {
  return await jsonGet(
    `llm-logs/${requestId}/events?types=ToolStarted&types=ToolCompleted&types=ToolFailed`
  );
}

//
// PROMPTS
//
// Profile
export const getPrompts = async () => await jsonGet(`prompts`);
export const getSinglePrompt = async (id) => await jsonGet(`prompts/${id}`);
export const createPrompt = async (dto) => await jsonPost(`prompts`, dto);
export const updatePrompt = async (id, dto) =>
  await jsonPut(`prompts/${id}`, dto);
export const deletePrompt = async (id) => await jsonDelete(`prompts/${id}`);

// //
// // BULK
// //
// export async function getBulkCalls() {
//   return await jsonGet(`bulk`);
// }

// export async function getBulkDetails(id) {
//   return await jsonGet(`bulk/${id}/details`);
// }

// export async function deleteBulkCall(id) {
//   return await jsonDelete(`bulk/${id}`);
// }

// export async function sendLlmMessagesBulk(dto) {
//   mixpanel.track("Bulk Messages Sent");
//   return await jsonPost(`bulk`, dto);
// }

// export async function getSupportedModels() {
//   return await jsonGet("models/descriptors");
// }

// export async function getSupportedModelDescriptors() {
//   return await jsonGet("models/descriptors");
// }

export async function getProviders() {
  return await jsonGet("providers");
}

// export async function userCreate(tenantId, dto) {
//   // dto: { emailAddress }
//   return await jsonPost(`tenants/${tenantId}/users`, dto);
// }

export async function getUsers() {
  return await jsonGet("users");
}

// export async function getUserById(userId) {
//   return await jsonGet(`users/${userId}`);
// }

export async function getUsersFiltered(
  page,
  pageSize,
  search = null,
  role = null,
  plan = null
) {
  return await jsonGet(
    `users?page=${page}&pageSize=${pageSize}&search=${search}&role=${role}&plan=${plan}`
  );
}

export const getSingleUser = async (id) => await jsonGet(`users/${id}`);
export const createUser = async (dto) => {
  mixpanel.track("User Created");
  return await jsonPost(`users`, dto);
};
export const updateUser = async (id, dto) => await jsonPut(`users/${id}`, dto);
export const deleteUser = async (id) => await jsonDelete(`users/${id}`);

export const sendUserInvitation = async (id) =>
  await jsonPost(`users/${id}/invitations`);

export const fetchInvite = async (token) =>
  await jsonGet(`invitations/${token}`);

export async function userConfirmation(dto) {
  return await jsonPost("auth/account/confirmation", dto);
}

// export const getThreads = async () => await jsonGet(`threads`);

// export const createThread = async (dto) => await jsonPost(`threads`, dto);

// export async function getUserThreads(userId) {
//   return await jsonGet(`users/${userId}/threads`);
// }

// export async function getThreadMessages(threadId) {
//   return await jsonGet(`threads/${threadId}/messages`);
// }

export async function getMessageLogs(messageId) {
  mixpanel.track("Get Logs");
  return await jsonGet(`messages/${messageId}/logs`);
}

// export async function postCustomerChat(customerId, message) {
//     return await jsonPost(`customers/${customerId}/messages`, message);
// }

//
// CORPUS
//

export async function vectorSearch(query) {
  return await jsonPost(`corpus/search/vector`, { query });
}

export async function classicSearch(query) {
  return await jsonPost(`corpus/search/classic`, { query });
}

export async function getCorpus() {
  return await jsonGet("corpus");
}

//
// Plans
//

export const planCreate = async (dto) => await jsonPost("plans", dto);
export const getPlans = async () => await jsonGet("plans");
export const getPlan = async (planId) => await jsonGet(`plans/${planId}`);
export const planEdit = async (planId, dto) =>
  await jsonPut(`plans/${planId}`, dto);

// Subscriptions
export const getSubscriptions = async () => await jsonGet("subscriptions");

export const getMentees = async () => await jsonGet("mentees");

export const createStripe = async () =>
  await jsonPost(`tenants/stripe-account`);
export const linkStripe = async () =>
  await jsonPost(`tenants/stripe-account/link`);

// Profile
export const getProfile = async () => {
  const profile = await jsonGet(`profile`);
  // mixpanel.identify(profile.userId);
  // mixpanel.people.set({
  //   $first_name: profile.firstName,
  //   $last_name: profile.lastName,
  //   $email: profile.emailAddress,
  //   $created: profile.created,
  //   $avatar: profile.profileImageUrl,
  //   plan: profile.activePlan,
  //   tenantId: profile.tenantId,
  //   role: profile.userRole,
  // });
  return profile;
};
export const updateProfile = async (dto) => await jsonPut(`profile`, dto);

export const getTenantProfile = async () => await jsonGet(`tenant/profile`);
export const updateTenantProfile = async (dto) =>
  await jsonPut(`tenant/profile`, dto);
export const getTenantApikeys = async () => await jsonGet(`tenant/apikeys`);
export const createApiKey = async (dto) => {
  mixpanel.track("API Key Created");
  return await jsonPost(`tenant/apikeys`, dto);
};
export const deleteApiKey = async (id) =>
  await jsonDelete(`tenant/apikeys/${id}`);

//
// Environments
//
export const getEnvironments = async () => await jsonGet(`environments`);
export const createEnvironment = async (dto) =>
  await jsonPost(`environments`, dto);
export const deleteEnvironment = async (id) =>
  await jsonDelete(`environments/${id}`);
export const getEnvironmentDiff = async (source, target) =>
  await jsonGet(`environments/diff?source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}`);
export const promoteEnvironment = async (dto) =>
  await jsonPost(`environments/promote`, dto);

//
// Flows
//

export const getFlows = async () => await jsonGet("flows");
export const getFlowsStatistics = async () => await jsonGet("flows/statistics");
export const getFlow = async (flowId) => await jsonGet(`flows/${flowId}`);
export const getParameters = async (flowId, versionId, entrypoint) =>
  await jsonGet(
    `flows/${flowId}/parameters?version=${versionId}${
      entrypoint ? `&entrypoint=${entrypoint}` : ""
    }`
  );
export const updateFlow = async (flowId, flow) =>
  await jsonPut(`flows/${flowId}`, flow);
// dto: { sampleRate: 0..1 | null (null removes the flow rate), until: ISO instant | null }
export const updateFlowDetailSampleRate = async (flowId, dto) =>
  await jsonPut(`flows/${flowId}/detail-sample-rate`, dto);
export const createFlow = async (dto) => await jsonPost("flows", dto);
export const insertExampleFlows = async () => await jsonPost("flows/examples");
export const duplicateFlow = async (flowId, versionId) =>
  await jsonPost(`flows/${flowId}/versions/${versionId}/duplicate`);
export const forkVersion = async (flowId, versionId) => {
  mixpanel.track("Flow Forked");
  return await jsonPost(`flows/${flowId}/versions/${versionId}/fork`);
};
export const runFlow = async (flowId, dto) => {
  mixpanel.track("Flow Run");
  return await jsonPost(`flows/${flowId}/runs`, dto);
};

export const deleteFlow = async (flowId) => {
  mixpanel.track("Flow Deleted");
  return await jsonDelete(`flows/${flowId}`);
};
export const deleteFlowVersion = async (flowId, versionId) => {
  mixpanel.track("Flow Version Deleted");
  return await jsonDelete(`flows/${flowId}/versions/${versionId}`);
};

// export const getFlowRuns = async (flowId) =>
//   await jsonGet(`flows/${flowId}/runs`);

export const activateVersion = async (flowId, versionId, environment, comment = null) => {
  mixpanel.track("Flow Version Activated");
  return await jsonPost(`flows/${flowId}/versions/${versionId}/environments`, {
    environment,
    comment,
  });
};

export const unpinEnvironment = async (flowId, environment) => {
  mixpanel.track("Flow Environment Unpinned");
  return await jsonDelete(
    `flows/${flowId}/environments?environment=${encodeURIComponent(environment)}`
  );
};

//
// Model Routing
//

// Model routing is now tenant-level (global), not per-flow
export const getModelRouting = async () =>
  await jsonGet(`model-routing`);

export const updateModelRouting = async (routingRules) => {
  mixpanel.track("Model Routing Updated");
  return await jsonPut(`model-routing`, routingRules);
};

//
// Prompt Templates
//

export const createPromptTemplate = async (flowId, dto) =>
  await jsonPost(`flows/${flowId}/prompt-templates`, dto);
export const updatePromptTemplate = async (flowId, promptTemplateId, dto) =>
  await jsonPut(`flows/${flowId}/prompt-templates/${promptTemplateId}`, dto);
export const deletePromptTemplate = async (flowId, promptTemplateId) =>
  await jsonDelete(`flows/${flowId}/prompt-templates/${promptTemplateId}`);
export const getPromptTemplates = async (flowId) =>
  await jsonGet(`flows/${flowId}/prompt-templates`);
export const getPromptTemplateVersions = async (flowId) =>
  await jsonGet(`flows/${flowId}/prompt-templates/versions`);
export const updateVersion = async (flowId, versionId, dto) =>
  await jsonPut(`flows/${flowId}/versions/${versionId}`, dto);
export const deleteVersion = async (flowId, versionId) =>
  await jsonDelete(`flows/${flowId}/versions/${versionId}`);
export const createVersion = async (flowId, dto) =>
  await jsonPost(`flows/${flowId}/versions`, dto);

export const getAgentGraph = async (flowId, versionId) =>
  await jsonGet(`flows/${flowId}/versions/${versionId}/agent-graph`);

// IMPORT / EXPORT
export const exportAll = async () => await jsonGet("export");
export const importFlowsAndTools = async (dto) => {
  mixpanel.track("Import Flows and Tools");
  return await jsonPost("export/import", dto);
};

export async function checkout(dto) {
  mixpanel.track("Checkout Credits");
  return await jsonPost("payment/stripe/checkout-credits", dto);
}

export async function customerPortal() {
  mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}

// AUTH
export async function requestPasswordReset(dto) {
  mixpanel.track("Request password reset");
  return await jsonPost("auth/account/request-password", dto);
}

export async function setPassword(dto) {
  mixpanel.track("Set password");
  return await jsonPut("auth/account/password", dto);
}

export async function uploadImage(file, imageType) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/profile/images/${imageType}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  return await response.json();
}

export async function login(dto) {
  var response = await fetch(`${prefix}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(dto),
  });

  if (!response.ok) {
    const message = `A login error has occured: ${response.status}`;
    throw new Error(message, { cause: response.status });
  }

  return await response.json();
}

/// BOILERPLATE API CALLS

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin-jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry-hard`);
export const getAdminJobs = async (
  page,
  pageSize,
  tenantFilter,
  statusFilter
) =>
  await jsonGet(
    `admin-jobs?page=${page}&pageSize=${pageSize}&tenantFilter=${tenantFilter}&statusFilter=${statusFilter}`
  );

export const adminImpersonateUser = async (dto) =>
  await jsonPost(`auth/account/impersonate`, dto);

// Redeem a one-time login token (e.g. an admin impersonation link) for a session token.
export const loginWithToken = async (token) =>
  await jsonPost(`auth/token`, { token });

// TENANTS
export const createTenant = async (dto) => await jsonPost(`tenants`, dto);
export const updateTenant = async (id, dto) =>
  await jsonPut(`tenants/${id}`, dto);
export const getTenants = async (page, pageSize) =>
  await jsonGet(`tenants?page=${page}&pageSize=${pageSize}`);
export const getTenant = async (id) => await jsonGet(`tenants/${id}`);
// Batch balance lookup for the tenant list's async credits column: returns { [tenantId]: credits }
export const getTenantsCredits = async (ids) =>
  await jsonGet(`tenants/credits?ids=${ids.join(",")}`);

//
// Feedback
//
export const submitFeedback = async (dto) => await jsonPost("feedback", dto);
export const getFeedbackForRequest = async (requestId) =>
  await jsonGet(`feedback/by-request/${requestId}`);
export const listFeedbacks = async (params) => {
  const qs = serializeParams(params);
  return await jsonGet(`feedback${qs ? `?${qs}` : ""}`);
};
export const getFlowFeedbackStats = async (flowName, period = 7) =>
  await jsonGet(`feedback/stats/${flowName}?period=${period}`);

// CREDITS
export const modifyCredits = async (tenantId, dto) => {
  // mix.track("Modify credits", { tenantId });
  return await jsonPost(`tenants/${tenantId}/credits`, dto);
};
// Low-balance threshold + automatic top-up, both in credits; null turns a setting off
export const updateTenantCreditSettings = async (tenantId, dto) =>
  await jsonPut(`tenants/${tenantId}/credit-settings`, dto);
